import { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import loader from '../../../assets/Images/loader.gif';

const UMP_SERVICE_API = 'https://oneml.spectra.co/getUmpServiceData';
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

// status '1' / close_in_crm 'Y' => SR closed in CRM, status '0' / close_in_crm 'N' => still open
const isClosed = (r) => String(r.status) === '1' || r.close_in_crm === 'Y';

const STATUS_FILTERS = [
    { key: 'All',    label: 'All' },
    { key: 'Closed', label: 'Closed in CRM', bg: '#d1f0d8', color: '#1a6b2e' },
    { key: 'Open',   label: 'Open',          bg: '#fff3cd', color: '#856404' },
];

// API dates look like "2026-09-29 12:11"; swap the space for "T" so Safari/iOS can parse them too
const parseDate = (value) => {
    if (!value) return null;
    const d = new Date(String(value).replace(' ', 'T'));
    return isNaN(d) ? null : d;
};

const timeDiff = (start, end) => {
    const a = parseDate(start);
    const b = parseDate(end);
    if (!a || !b) return '—';
    const totalMins = Math.floor(Math.abs(b - a) / 60000);
    const days  = Math.floor(totalMins / 1440);
    const hours = Math.floor(totalMins / 60) % 24;
    const mins  = totalMins % 60;
    const parts = [];
    if (days  > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (mins  > 0 || parts.length === 0) parts.push(`${mins}m`);
    return parts.join(' ');
};

const copyToClipboard = async (text) => {
    try {
        if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(text);
        } else {
            // fallback for http / older webviews
            const ta = document.createElement('textarea');
            ta.value = text;
            ta.style.position = 'fixed';
            ta.style.opacity = '0';
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
        }
        toast.success('Message ID copied', { position: toast.POSITION.BOTTOM_CENTER, autoClose: 1200 });
    } catch (error) {
        console.error('copy error:', error);
        toast.error('Unable to copy', { position: toast.POSITION.BOTTOM_CENTER, autoClose: 1500 });
    }
};

const Badge = ({ bg, color, children }) => (
    <span style={{
        display: 'inline-block', padding: '2px 10px', borderRadius: '12px',
        fontSize: '0.72rem', fontWeight: 600, background: bg, color,
        whiteSpace: 'nowrap', letterSpacing: '0.03em',
    }}>
        {children}
    </span>
);

const StatusBadge = ({ row }) => (
    isClosed(row)
        ? <Badge bg="#d1f0d8" color="#1a6b2e">Closed</Badge>
        : <Badge bg="#fff3cd" color="#856404">Open</Badge>
);

const UmpSrDetails = () => {
    const [allData, setAllData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasFetched, setHasFetched] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [activeStatus, setActiveStatus] = useState('All');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                const response = await fetch(UMP_SERVICE_API, {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                });
                const result = await response.json();
                const rows = result?.data || (Array.isArray(result) ? result : []);
                if (rows.length > 0) {
                    setAllData(rows);
                } else {
                    toast.warning(result?.meta?.Message || 'No data found', {
                        position: toast.POSITION.BOTTOM_CENTER, autoClose: 2000,
                    });
                }
            } catch (error) {
                console.error('getUmpServiceData error:', error);
                toast.error('Failed to fetch UMP SR data.', {
                    position: toast.POSITION.BOTTOM_CENTER, autoClose: 2000,
                });
            } finally {
                setIsLoading(false);
                setHasFetched(true);
            }
        };
        fetchData();
    }, []);

    useEffect(() => { setPage(1); }, [searchInput, activeStatus, pageSize, dateFrom, dateTo]);

    // Search + date filters, before the status filter, so the pill counts reflect the other filters
    const baseFiltered = useMemo(() => {
        const term = searchInput.trim().toUpperCase();
        return allData.filter((r) => {
            if (term) {
                const mobile = String(r.mobile_number || '');
                const sr = String(r.sr_number || '').toUpperCase();
                const messageId = String(r.message_id || '').toUpperCase();
                if (!mobile.includes(term) && !sr.includes(term) && !messageId.includes(term)) return false;
            }
            // created_on starts with "YYYY-MM-DD", so a plain string compare works for the date range
            const day = String(r.created_on || '').slice(0, 10);
            if (dateFrom && (!day || day < dateFrom)) return false;
            if (dateTo && (!day || day > dateTo)) return false;
            return true;
        });
    }, [allData, searchInput, dateFrom, dateTo]);

    const counts = useMemo(() => {
        const closed = baseFiltered.filter(isClosed).length;
        return { All: baseFiltered.length, Closed: closed, Open: baseFiltered.length - closed };
    }, [baseFiltered]);

    const filteredData = useMemo(() => {
        if (activeStatus === 'All') return baseFiltered;
        const wantClosed = activeStatus === 'Closed';
        return baseFiltered.filter((r) => isClosed(r) === wantClosed);
    }, [baseFiltered, activeStatus]);

    const totalPages = Math.max(1, Math.ceil(filteredData.length / pageSize));
    const pageData = filteredData.slice((page - 1) * pageSize, page * pageSize);
    const startRecord = filteredData.length === 0 ? 0 : (page - 1) * pageSize + 1;
    const endRecord = Math.min(page * pageSize, filteredData.length);
    const isFiltered = searchInput || activeStatus !== 'All' || dateFrom || dateTo;

    const pageNumbers = useMemo(() => {
        const pages = [];
        for (let i = 1; i <= totalPages; i++) {
            if (i === 1 || i === totalPages || (i >= page - 1 && i <= page + 1)) {
                pages.push(i);
            } else if (pages[pages.length - 1] !== '...') {
                pages.push('...');
            }
        }
        return pages;
    }, [totalPages, page]);

    const exportCSV = () => {
        if (filteredData.length === 0) return;
        const headers = ['SR Number', 'Mobile Number', 'Message ID', 'Created On', 'Updated On', 'Time To Close', 'Status', 'Close In CRM'];
        const escape = (val) => {
            const s = String(val ?? '').replace(/"/g, '""');
            return /[",\n]/.test(s) ? `"${s}"` : s;
        };
        const rows = filteredData.map((r) => [
            r.sr_number,
            r.mobile_number,
            r.message_id,
            r.created_on,
            r.updated_on || '',
            isClosed(r) ? timeDiff(r.created_on, r.updated_on) : '',
            isClosed(r) ? 'Closed' : 'Open',
            r.close_in_crm,
        ].map(escape).join(','));
        const csv = [headers.map(escape).join(','), ...rows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ump_sr_details_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const closeRate = counts.All ? Math.round((counts.Closed / counts.All) * 100) : 0;

    return (
        <>
            {isLoading && (
                <div className="loader-wrapper">
                    <div className="loader">
                        <img src={loader} alt="loading..." />
                    </div>
                </div>
            )}

            {/* Filter Panel */}
            <div className="Searchbar-bg" style={{
                padding: '8px 16px',
                marginTop: '63px',
                marginBottom: '16px',
                position: 'sticky',
                top: '63px',
                zIndex: 999,
            }}>
                <div className="d-flex align-items-center gap-3 flex-wrap">
                    {/* Search */}
                    <div style={{ position: 'relative', flex: '1 1 200px', minWidth: '180px', maxWidth: '340px' }}>
                        <i className="fa-solid fa-magnifying-glass" style={{
                            position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)',
                            color: '#bbb', fontSize: '0.78rem', pointerEvents: 'none',
                        }} />
                        <input
                            type="text"
                            placeholder="Search SR number, mobile or message ID…"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            style={{
                                width: '100%', padding: '8px 30px 8px 30px',
                                borderRadius: '6px', border: 'none',
                                fontSize: '0.83rem', background: '#fff',
                                outline: 'none',
                            }}
                        />
                        {searchInput && (
                            <button onClick={() => setSearchInput('')} style={{
                                position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)',
                                background: 'none', border: 'none', color: '#aaa', cursor: 'pointer', padding: 0,
                            }}>
                                <i className="fa-solid fa-xmark" style={{ fontSize: '0.75rem' }} />
                            </button>
                        )}
                    </div>

                    {/* Date range (on Created On) */}
                    {/* <div className="d-flex align-items-center gap-2" style={{ flexWrap: 'nowrap' }}>
                        <div style={{ position: 'relative' }}>
                            <i className="fa-regular fa-calendar" style={dateIconStyle} />
                            <input
                                type="date"
                                value={dateFrom}
                                max={dateTo || undefined}
                                onChange={(e) => setDateFrom(e.target.value)}
                                style={{ ...dateInputStyle, color: dateFrom ? '#333' : '#999' }}
                            />
                        </div>
                        <span style={{ color: 'var(--bg-color)', fontSize: '0.9rem', fontWeight: 500 }}>→</span>
                        <div style={{ position: 'relative' }}>
                            <i className="fa-regular fa-calendar" style={dateIconStyle} />
                            <input
                                type="date"
                                value={dateTo}
                                min={dateFrom || undefined}
                                onChange={(e) => setDateTo(e.target.value)}
                                style={{ ...dateInputStyle, color: dateTo ? '#333' : '#999' }}
                            />
                        </div>
                        {(dateFrom || dateTo) && (
                            <button onClick={() => { setDateFrom(''); setDateTo(''); }} style={{
                                background: '#f0eaf8', border: 'none',
                                borderRadius: '6px', color: 'var(--bg-color)', padding: '5px 9px',
                                cursor: 'pointer', fontSize: '0.75rem', lineHeight: 1,
                            }} title="Clear dates">
                                <i className="fa-solid fa-xmark" />
                            </button>
                        )}
                    </div> */}
                </div>
            </div>

            <div className="container-fluid px-3">

                {/* Summary cards */}
                {/* {hasFetched && allData.length > 0 && (
                    <div className="row g-2 mb-3">
                        <SummaryCard icon="fa-solid fa-list-check" label="Total SRs" value={counts.All} color="var(--bg-color)" />
                        <SummaryCard icon="fa-solid fa-circle-check" label="Closed in CRM" value={counts.Closed} color="#1a6b2e" />
                        <SummaryCard icon="fa-solid fa-hourglass-half" label="Open" value={counts.Open} color="#856404" />
                        <SummaryCard icon="fa-solid fa-percent" label="Close Rate" value={`${closeRate}%`} color="#004085" />
                    </div>
                )} */}

                {/* Status filter pills */}
                {hasFetched && allData.length > 0 && (
                    <div className="d-flex flex-wrap gap-2 mb-3">
                        {STATUS_FILTERS.map(({ key, label, bg, color }) => {
                            const isActive = activeStatus === key;
                            const activeBg = key === 'All' ? 'var(--bg-color)' : bg;
                            const activeColor = key === 'All' ? '#fff' : color;
                            return (
                                <button
                                    key={key}
                                    onClick={() => setActiveStatus(key)}
                                    style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                                        borderRadius: '20px',
                                        border: isActive ? 'none' : '1px solid #dde',
                                        background: isActive ? activeBg : '#fff',
                                        color: isActive ? activeColor : '#666',
                                        fontWeight: isActive ? 700 : 400,
                                        fontSize: '0.78rem',
                                        padding: '5px 14px',
                                        cursor: 'pointer',
                                        transition: 'all 0.15s',
                                        whiteSpace: 'nowrap',
                                        boxShadow: isActive ? '0 3px 10px rgba(0,0,0,0.15)' : '0 1px 3px rgba(0,0,0,0.07)',
                                    }}
                                >
                                    {label}
                                    <span style={{
                                        fontSize: '0.68rem', fontWeight: 700,
                                        background: isActive ? 'rgba(255,255,255,0.25)' : '#f0eaf8',
                                        color: isActive ? activeColor : 'var(--bg-color)',
                                        borderRadius: '10px', padding: '1px 7px',
                                        minWidth: '22px', textAlign: 'center',
                                    }}>
                                        {counts[key]}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                )}

                {/* Toolbar */}
                {hasFetched && !isLoading && allData.length > 0 && (
                    <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2"
                        style={{ background: '#f7f4fc', borderRadius: '10px', padding: '8px 14px', border: '1px solid #ede8f8' }}>
                        <div className="d-flex align-items-center gap-2">
                            <span style={{
                                background: 'var(--bg-color)', color: '#fff',
                                borderRadius: '8px', padding: '2px 10px',
                                fontSize: '0.72rem', fontWeight: 700,
                            }}>
                                {filteredData.length}
                            </span>
                            <small style={{ color: '#888', fontSize: '0.78rem' }}>
                                {filteredData.length === 0
                                    ? 'no records match'
                                    : `records${isFiltered ? ` · filtered from ${allData.length}` : ''} · showing ${startRecord}–${endRecord}`}
                            </small>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <div className="d-flex align-items-center gap-1">
                                <small style={{ color: '#aaa', fontSize: '0.72rem', marginRight: '2px' }}>Rows:</small>
                                {PAGE_SIZE_OPTIONS.map((s) => (
                                    <button
                                        key={s}
                                        onClick={() => setPageSize(s)}
                                        style={{
                                            padding: '2px 8px', fontSize: '0.75rem', borderRadius: '6px',
                                            border: 'none', cursor: 'pointer',
                                            background: pageSize === s ? 'var(--bg-color)' : '#ede8f8',
                                            color: pageSize === s ? '#fff' : 'var(--bg-color)',
                                            fontWeight: pageSize === s ? 700 : 400,
                                            transition: 'all 0.12s',
                                        }}
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>
                            <button
                                onClick={exportCSV}
                                disabled={filteredData.length === 0}
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '5px',
                                    padding: '5px 14px', fontSize: '0.78rem', borderRadius: '8px',
                                    border: 'none', cursor: filteredData.length === 0 ? 'not-allowed' : 'pointer',
                                    background: filteredData.length === 0
                                        ? '#e9e9e9'
                                        : 'linear-gradient(135deg, #28a745 0%, #1a7a30 100%)',
                                    color: filteredData.length === 0 ? '#aaa' : '#fff',
                                    fontWeight: 600,
                                    boxShadow: filteredData.length > 0 ? '0 2px 8px rgba(40,167,69,0.3)' : 'none',
                                    transition: 'all 0.15s',
                                }}
                                title="Export filtered data as CSV"
                            >
                                <i className="fa-solid fa-file-csv" />
                                Export CSV
                            </button>
                        </div>
                    </div>
                )}

                {/* No data */}
                {hasFetched && !isLoading && allData.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '64px 20px' }}>
                        <div style={{
                            width: '68px', height: '68px', borderRadius: '50%',
                            background: 'linear-gradient(135deg, #f0eaf8, #e2d5f4)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            margin: '0 auto 14px',
                        }}>
                            <i className="fa-solid fa-clipboard-list" style={{ fontSize: '2rem', color: 'var(--bg-color)', opacity: 0.45 }} />
                        </div>
                        <p style={{ fontSize: '0.95rem', color: '#aaa', margin: 0 }}>No UMP SR data available.</p>
                    </div>
                )}

                {/* No match */}
                {hasFetched && !isLoading && allData.length > 0 && filteredData.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '54px 20px' }}>
                        <div style={{
                            width: '58px', height: '58px', borderRadius: '50%',
                            background: '#f5f5f5',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            margin: '0 auto 12px',
                        }}>
                            <i className="fa-solid fa-magnifying-glass" style={{ fontSize: '1.4rem', color: '#ccc' }} />
                        </div>
                        <p style={{ fontSize: '0.9rem', color: '#aaa', margin: '0 0 4px' }}>No records match your filters.</p>
                        <small style={{ color: '#bbb' }}>Try adjusting the search, status or date range.</small>
                    </div>
                )}

                {/* Main Table */}
                {hasFetched && !isLoading && pageData.length > 0 && (
                    <div style={{ borderRadius: '12px', overflow: 'hidden', boxShadow: '0 3px 14px rgba(0,0,0,0.09)' }}>
                        <div className="table-responsive">
                            <table className="table table-hover mb-0" style={{ fontSize: '0.82rem' }}>
                                <thead>
                                    <tr style={{ backgroundColor: 'var(--bg-color)', color: '#fff' }}>
                                        <th style={{ ...thStyle, width: '42px', textAlign: 'center' }}>#</th>
                                        <th style={thStyle}>SR Number</th>
                                        <th style={thStyle}>Mobile Number</th>
                                        <th style={thStyle}>Created On</th>
                                        <th style={thStyle}>Updated On</th>
                                        <th style={thStyle}>Time To Close</th>
                                        <th style={thStyle}>Status</th>
                                        <th style={thStyle}>Message ID</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pageData.map((r, idx) => {
                                        const closed = isClosed(r);
                                        const diff = closed ? timeDiff(r.created_on, r.updated_on) : '—';
                                        return (
                                            <tr key={r.id ?? idx} style={{ verticalAlign: 'middle', background: idx % 2 === 0 ? '#fff' : '#faf8fd' }}>
                                                <td style={{ ...tdStyle, textAlign: 'center', color: '#ccc', fontWeight: 700, width: '42px' }}>
                                                    {startRecord + idx}
                                                </td>
                                                <td style={{ ...tdStyle, fontWeight: 600, color: 'var(--bg-color)' }}>{r.sr_number || '—'}</td>
                                                <td style={tdStyle}>{r.mobile_number || '—'}</td>
                                                <td style={tdStyle}>{r.created_on || '—'}</td>
                                                <td style={{ ...tdStyle, color: r.updated_on ? '#444' : '#bbb' }}>{r.updated_on || '—'}</td>
                                                <td style={tdStyle}>
                                                    <span style={{
                                                        display: 'inline-block', padding: '2px 9px', borderRadius: '10px',
                                                        background: diff === '—' ? '#f2f2f2' : '#eaf4ff',
                                                        color: diff === '—' ? '#bbb' : '#004085',
                                                        fontSize: '0.73rem', fontWeight: 600,
                                                    }}>
                                                        {diff}
                                                    </span>
                                                </td>
                                                <td style={tdStyle}><StatusBadge row={r} /></td>
                                                <td style={{ ...tdStyle, maxWidth: 'none', overflow: 'visible' }}>
                                                    {r.message_id ? (
                                                        <span className="d-inline-flex align-items-center gap-2">
                                                            <span style={{ color: '#666', fontFamily: 'monospace', fontSize: '0.74rem', userSelect: 'all' }}>
                                                                {r.message_id}
                                                            </span>
                                                            <button
                                                                onClick={() => copyToClipboard(String(r.message_id))}
                                                                title="Copy Message ID"
                                                                style={{
                                                                    background: '#f0eaf8', border: 'none', borderRadius: '6px',
                                                                    color: 'var(--bg-color)', padding: '3px 7px', cursor: 'pointer',
                                                                    fontSize: '0.72rem', lineHeight: 1,
                                                                }}
                                                            >
                                                                <i className="fa-regular fa-copy" />
                                                            </button>
                                                        </span>
                                                    ) : '—'}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Pagination */}
                {hasFetched && !isLoading && filteredData.length > 0 && totalPages > 1 && (
                    <div className="d-flex justify-content-center align-items-center mt-3 mb-3 gap-1 flex-wrap">
                        <button style={pagerBtn(false, page === 1)} onClick={() => setPage(1)} disabled={page === 1} title="First">
                            <i className="fa-solid fa-angles-left" />
                        </button>
                        <button style={pagerBtn(false, page === 1)} onClick={() => setPage((p) => p - 1)} disabled={page === 1} title="Prev">
                            <i className="fa-solid fa-angle-left" />
                        </button>
                        {pageNumbers.map((p, i) =>
                            p === '...' ? (
                                <span key={`e-${i}`} style={{ padding: '0 4px', color: '#bbb', fontSize: '0.85rem' }}>…</span>
                            ) : (
                                <button key={p} style={pagerBtn(p === page, false)} onClick={() => setPage(p)}>{p}</button>
                            )
                        )}
                        <button style={pagerBtn(false, page === totalPages)} onClick={() => setPage((p) => p + 1)} disabled={page === totalPages} title="Next">
                            <i className="fa-solid fa-angle-right" />
                        </button>
                        <button style={pagerBtn(false, page === totalPages)} onClick={() => setPage(totalPages)} disabled={page === totalPages} title="Last">
                            <i className="fa-solid fa-angles-right" />
                        </button>
                    </div>
                )}
            </div>
        </>
    );
};

const SummaryCard = ({ icon, label, value, color }) => (
    <div className="col-6 col-md-3">
        <div style={{
            background: '#fff', borderRadius: '12px', padding: '12px 14px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.07)', borderLeft: `4px solid ${color}`,
            display: 'flex', alignItems: 'center', gap: '12px', height: '100%',
        }}>
            <div style={{
                width: '36px', height: '36px', borderRadius: '50%', background: '#f7f4fc',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
                <i className={icon} style={{ color, fontSize: '0.95rem' }} />
            </div>
            <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '0.72rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
                <div style={{ fontSize: '1.15rem', fontWeight: 700, color }}>{value}</div>
            </div>
        </div>
    </div>
);

const dateIconStyle = {
    position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)',
    color: '#bbb', fontSize: '0.72rem', pointerEvents: 'none',
};

const dateInputStyle = {
    padding: '8px 10px 8px 26px', borderRadius: '6px', border: 'none',
    fontSize: '0.8rem', background: '#fff',
    outline: 'none', width: '148px',
};

const thStyle = {
    whiteSpace: 'nowrap',
    padding: '10px 12px',
    fontWeight: 600,
    fontSize: '0.77rem',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    border: 'none',
};

const tdStyle = {
    whiteSpace: 'nowrap',
    padding: '9px 12px',
    color: '#444',
    maxWidth: '220px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    borderColor: '#f0ecfa',
};

const pagerBtn = (active, disabled) => ({
    padding: '5px 11px',
    fontSize: '0.78rem',
    borderRadius: '8px',
    border: 'none',
    cursor: disabled ? 'not-allowed' : 'pointer',
    background: active ? 'var(--bg-color)' : disabled ? '#f0f0f0' : '#f0eaf8',
    color: active ? '#fff' : disabled ? '#bbb' : 'var(--bg-color)',
    fontWeight: active ? 700 : 400,
    minWidth: '34px',
    textAlign: 'center',
    transition: 'all 0.12s',
    boxShadow: active ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
});

export default UmpSrDetails;
